import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Element } from '../models/Element';
import { TextElement } from '../models/elements/TextElement';
import { VideoElement } from '../models/elements/VideoElement';
import { ImageElement } from '../models/elements/ImageElement';

interface TimelineElementProps {
  element: Element;
  timelineScale: number;
  isSelected: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMoveElement?: (elementId: string, newStartTime: number) => void;
  onTrimElementStart?: (elementId: string, newStartTime: number) => void;
  onTrimElementEnd?: (elementId: string, newEndTime: number) => void;
}

export const TimelineElement = observer(
  ({
    element,
    timelineScale,
    isSelected,
    onClick,
    onMoveElement,
    onTrimElementStart,
    onTrimElementEnd,
  }: TimelineElementProps) => {
    // Reference to the element container
    const elementRef = useRef<HTMLDivElement>(null);

    // Element position and size
    const elementDuration =
      typeof element.duration === 'number' ? element.duration : 0;
    const left = element.time * timelineScale;
    const width = elementDuration * timelineScale;

    // Color based on element type
    const getColor = () => {
      switch (element.type) {
        case 'video':
          return '#006FEE';
        case 'text':
          return '#CBD7F0';
        case 'audio':
          return '#7828C8';
        case 'image':
          return '#17C964';
        default:
          return '#3F4043';
      }
    };

    // Content preview
    const getPreview = () => {
      if (element.type === 'text') {
        const textElement = element as TextElement;
        return (
          textElement.text.substring(0, 20) +
          (textElement.text.length > 20 ? '...' : '')
        );
      }

      if (element.type === 'video') {
        const videoElement = element as VideoElement;
        return videoElement.source ? 'Video' : 'No source';
      }

      if (element.type === 'image') {
        const imageElement = element as ImageElement;
        return imageElement.source ? 'Image' : 'No source';
      }

      return element.type;
    };

    // Moving element
    const handleMoveStart = (e: React.MouseEvent) => {
      if (!isSelected || !onMoveElement) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startTime = element.time;

      const handleMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        const deltaX = moveEvent.clientX - startX;
        const deltaTime = deltaX / timelineScale;

        if (elementRef.current) {
          elementRef.current.style.transform = `translateX(${deltaX}px)`;
        }
      };

      const handleMoveEnd = (upEvent: MouseEvent) => {
        upEvent.preventDefault();

        if (elementRef.current) {
          elementRef.current.style.transform = '';
        }

        const deltaX = upEvent.clientX - startX;
        const deltaTime = deltaX / timelineScale;
        const newTime = Math.max(0, startTime + deltaTime);

        // Only apply if we moved at least 3 pixels
        if (Math.abs(deltaX) >= 3) {
          onMoveElement(element.id, newTime);
        }

        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleMoveEnd);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleMoveEnd);
    };

    // Trimming element start
    const handleTrimStartStart = (e: React.MouseEvent) => {
      if (!onTrimElementStart) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startTime = element.time;
      const startDuration = elementDuration;

      const handleTrimMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        const deltaX = moveEvent.clientX - startX;
        const deltaTime = deltaX / timelineScale;

        // Don't allow trimming beyond element end
        const maxDelta = startDuration - 0.1;
        const actualDeltaTime = Math.min(deltaTime, maxDelta);
        const newTime = Math.max(0, startTime + actualDeltaTime);
        const newDuration = startDuration - (newTime - startTime);

        if (elementRef.current) {
          elementRef.current.style.transform = `translateX(${
            (newTime - startTime) * timelineScale
          }px)`;
          elementRef.current.style.width = `${newDuration * timelineScale}px`;
        }
      };

      const handleTrimEnd = (upEvent: MouseEvent) => {
        upEvent.preventDefault();

        if (elementRef.current) {
          elementRef.current.style.transform = '';
          elementRef.current.style.width = '';
        }

        const deltaX = upEvent.clientX - startX;
        const deltaTime = deltaX / timelineScale;

        // Don't allow trimming beyond element end
        const maxDelta = startDuration - 0.1;
        const actualDeltaTime = Math.min(deltaTime, maxDelta);
        const newTime = Math.max(0, startTime + actualDeltaTime);

        // Only apply if we moved at least 3 pixels
        if (Math.abs(deltaX) >= 3) {
          onTrimElementStart(element.id, newTime);
        }

        document.removeEventListener('mousemove', handleTrimMove);
        document.removeEventListener('mouseup', handleTrimEnd);
      };

      document.addEventListener('mousemove', handleTrimMove);
      document.addEventListener('mouseup', handleTrimEnd);
    };

    // Trimming element end
    const handleTrimEndStart = (e: React.MouseEvent) => {
      if (!onTrimElementEnd) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startTime = element.time;
      const startDuration = elementDuration;

      const handleTrimMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        const deltaX = moveEvent.clientX - startX;
        const deltaTime = deltaX / timelineScale;

        // Don't allow trimming below minimum duration
        const newDuration = Math.max(0.1, startDuration + deltaTime);

        if (elementRef.current) {
          elementRef.current.style.width = `${newDuration * timelineScale}px`;
        }
      };

      const handleTrimEnd = (upEvent: MouseEvent) => {
        upEvent.preventDefault();

        if (elementRef.current) {
          elementRef.current.style.width = '';
        }

        const deltaX = upEvent.clientX - startX;
        const deltaTime = deltaX / timelineScale;

        // Calculate new end time
        const newDuration = Math.max(0.1, startDuration + deltaTime);
        const newEndTime = startTime + newDuration;

        // Only apply if we moved at least 3 pixels
        if (Math.abs(deltaX) >= 3) {
          onTrimElementEnd(element.id, newEndTime);
        }

        document.removeEventListener('mousemove', handleTrimMove);
        document.removeEventListener('mouseup', handleTrimEnd);
      };

      document.addEventListener('mousemove', handleTrimMove);
      document.addEventListener('mouseup', handleTrimEnd);
    };

    const handleElementClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // Log element click and call the onClick handler for selection
      console.log(`Element clicked: ${element.id}`);
      
      // IMPORTANT: Let click event bubble up to track content for playhead movement
      // Do NOT call e.stopPropagation() here!
      
      onClick(e);
    };

    // Determine if this is an image element
    const isImageElement = element.type === 'image';

    // Get image source for image elements
    const getImageSource = () => {
      if (isImageElement) {
        const imageElement = element as ImageElement;
        return imageElement.source;
      }
      return null;
    };
    
    const imageSource = getImageSource();
    
    return (
      <ElementContainer
        ref={elementRef}
        style={{ left: `${left}px`, width: `${width}px` }}
        color={getColor()}
        isSelected={isSelected}
        onClick={handleElementClick}
        className="timeline-element"
        isImage={isImageElement}
      >
        {isSelected && (
          <>
            <TrimHandle position="left" onMouseDown={handleTrimStartStart} />
            <TrimHandle position="right" onMouseDown={handleTrimEndStart} />
          </>
        )}

        {isImageElement && imageSource ? (
          <ElementImageContent onMouseDown={handleMoveStart}>
            <ElementImage src={imageSource} />
            <ElementImageLabel>{getPreview()}</ElementImageLabel>
          </ElementImageContent>
        ) : (
          <ElementContent 
            onMouseDown={handleMoveStart}
            isImage={isImageElement}
          >
            {getPreview()}
          </ElementContent>
        )}
      </ElementContainer>
    );
  },
);

const ElementContainer = styled.div<{
  color: string;
  isSelected: boolean;
  isImage?: boolean;
}>`
  position: absolute;
  height: 80%;
  top: 10%;
  background-color: ${(props) => props.color};
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  border: ${(props) => (props.isSelected ? '2px solid #FFF' : props.isImage ? '1px solid rgba(255,255,255,0.3)' : 'none')};
  user-select: none; /* Prevent text selection during drag */
  touch-action: none; /* Improve touch interactions */
  z-index: 10;
  will-change: transform, width; /* Optimize for transforms */

  &:hover {
    filter: brightness(1.2);
  }
`;


const ElementContent = styled.div<{
  isImage?: boolean;
}>`
  padding: 4px 8px;
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 100%;
  cursor: move;
  user-select: none; /* Prevent text selection */
  pointer-events: all; /* Ensure clicks are captured */
  text-shadow: ${props => props.isImage ? '0px 0px 2px rgba(0, 0, 0, 0.8)' : 'none'};
  font-weight: ${props => props.isImage ? 'bold' : 'normal'};
  display: flex;
  align-items: center;
`;

const ElementImageContent = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  cursor: move;
  user-select: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const ElementImage = styled.div<{ src: string }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-image: ${props => `url(${props.src})`};
  background-size: auto 100%;
  background-repeat: repeat-x;
  background-position: left center;
  opacity: 0.9;
`;

const ElementImageLabel = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding: 2px 6px;
  font-size: 10px;
  color: white;
  background-color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0px 0px 2px rgba(0, 0, 0, 1);
  width: 100%;
  box-sizing: border-box;
`;

const TrimHandle = styled.div<{
  position: 'left' | 'right';
}>`
  position: absolute;
  width: 14px; /* Increase width for easier grabbing */
  height: 100%;
  top: 0;
  ${(props) =>
    props.position === 'left'
      ? 'left: -7px;'
      : 'right: -7px;'} /* Extend beyond element */
  cursor: ${(props) => (props.position === 'left' ? 'w-resize' : 'e-resize')};
  background-color: rgba(
    255,
    255,
    255,
    0.01
  ); /* Nearly invisible background for larger hit area */
  z-index: 20; /* Above the element */
  touch-action: none; /* Improve touch interactions */

  &:after {
    content: '';
    position: absolute;
    top: 10%;
    bottom: 10%;
    width: 4px;
    background-color: white;
    ${(props) => (props.position === 'left' ? 'right: 3px;' : 'left: 3px;')}
    opacity: 0.7;
    border-radius: 2px;
  }

  &:hover {
    &:after {
      top: 5%;
      bottom: 5%;
      width: 6px;
      opacity: 1;
    }
  }
`;
